import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/components/shared/form-dialog';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EntityCombobox, type EntityOption } from '@/components/shared/entity-combobox';
import {
  ScriptureRefPicker,
  scriptureRefPickerLabels,
} from '@/components/shared/scripture-ref-picker';
import {
  isOvernightProgramTimeRange,
  isProgramTimeRangeInvalid,
} from '@/components/programs/program-time';
import { Tip } from '@/components/shared/tip';
import { VenueSelect } from '@/components/shared/venue-select';
import { FormFieldError } from '@/components/ui/form-field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { Textarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-picker';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDate } from '@/utils/format';

interface MinisterOption {
  memberId: string;
  memberName: string;
  title: string;
  role: string;
  isActive: boolean;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

const guestSpeakerId = (name: string) => `guest:${name}`;

import {
  datesInProgramSpan,
  type ProgramDayDetail,
} from './program-day-detail';
import { buildWorshipMemberOptions } from './worship-member-options';
import { ProgramDayImageField } from './program-image-fields';
import { WorshipPlanningSection, type WorshipPlanningHandle } from './worship-planning-section';

export interface ProgramDayDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string;
  scheduleType: string;
  startsOn: string | null;
  endsOn: string | null;
  /** Scheduled dates for recurring programs. */
  occurrenceDates?: string[];
  defaultStartTime: string | null;
  defaultEndTime: string | null;
  defaultVenueId: string | null;
  /** Dates already assigned to other day details (exclude when creating). */
  takenDates?: string[];
  detail?: ProgramDayDetail | null;
  /** Services: worship team is planned on the added day, not on the service root. */
  enableWorship?: boolean;
}

export const ProgramDayDetailDialog = ({
  open,
  onOpenChange,
  programId,
  scheduleType,
  startsOn,
  endsOn,
  occurrenceDates = [],
  defaultStartTime,
  defaultEndTime,
  defaultVenueId,
  takenDates = [],
  detail = null,
  enableWorship = false,
}: ProgramDayDetailDialogProps) => {
  const { t } = useTranslation('programs');
  const { t: tContent } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const editing = detail;

  const spanDates = useMemo(() => {
    if (scheduleType === 'one_off' && startsOn) {
      const end = endsOn ?? startsOn;
      return datesInProgramSpan(startsOn, end);
    }
    return occurrenceDates;
  }, [scheduleType, startsOn, endsOn, occurrenceDates]);

  const selectableDates = useMemo(() => {
    if (editing) return spanDates;
    return spanDates.filter((date) => !takenDates.includes(date));
  }, [spanDates, takenDates, editing]);

  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [speakerId, setSpeakerId] = useState<string | null>(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState<EntityOption | null>(null);
  const [speakerQuery, setSpeakerQuery] = useState('');
  const [theme, setTheme] = useState('');
  const [reading1, setReading1] = useState('');
  const [reading2, setReading2] = useState('');
  const [reading3, setReading3] = useState('');
  const [description, setDescription] = useState('');
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const initialImageKeyRef = useRef<string | null>(null);
  const [startTime, setStartTime] = useState(defaultStartTime ?? '10:00');
  const [endTime, setEndTime] = useState(defaultEndTime ?? '12:00');
  const [venueId, setVenueId] = useState<string | null>(defaultVenueId);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const worshipRef = useRef<WorshipPlanningHandle>(null);

  const ministers = useQuery({
    queryKey: ['people', 'ministers', 'day-detail-picker'],
    queryFn: () => api.get<{ data: MinisterOption[] }>('/people/ministers?pageSize=100'),
    enabled: open,
  });

  const members = useQuery({
    queryKey: ['people', 'members', 'day-detail-picker', speakerQuery],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (speakerQuery.trim()) params.set('q', speakerQuery.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
    enabled: open && !enableWorship,
  });

  const applyReadings = (refs: string[] | null | undefined, legacy?: string | null) => {
    const list =
      refs && refs.length > 0
        ? refs
        : legacy?.trim()
          ? [legacy.trim()]
          : [];
    setReading1(list[0] ?? '');
    setReading2(list[1] ?? '');
    setReading3(list[2] ?? '');
  };

  const applySpeaker = (row: {
    speakerMemberId?: string | null;
    speakerMemberName?: string | null;
    guestSpeaker?: string | null;
  }) => {
    const ministerRows = ministers.data?.data ?? [];

    if (row.speakerMemberId) {
      const minister = ministerRows.find((item) => item.memberId === row.speakerMemberId);
      const label =
        minister
          ? `${minister.title} ${minister.memberName}`.trim()
          : (row.speakerMemberName?.trim() ??
            row.guestSpeaker?.trim() ??
            t('dayDetails.speakerSelected'));
      const option = {
        id: row.speakerMemberId,
        label,
        description: minister ? minister.role : undefined,
      };
      setSpeakerId(option.id);
      setSelectedSpeaker(option);
      return;
    }

    const guest = row.guestSpeaker?.trim();
    if (guest) {
      const option = { id: guestSpeakerId(guest), label: guest };
      setSpeakerId(option.id);
      setSelectedSpeaker(option);
      return;
    }
    setSpeakerId(null);
    setSelectedSpeaker(null);
  };

  useEffect(() => {
    if (!open) return;
    if (detail) {
      setSelectedDates(detail.dates);
      applySpeaker(detail);
      setTheme(detail.theme ?? '');
      applyReadings(detail.scriptureRefs, detail.scriptureRef);
      setDescription(detail.description ?? '');
      initialImageKeyRef.current = detail.imageKey;
      setImageKey(detail.imageKey);
      setImagePreview(detail.imageUrl);
      setStartTime(detail.startTime ?? defaultStartTime ?? '10:00');
      setEndTime(detail.endTime ?? defaultEndTime ?? '12:00');
      setVenueId(detail.venueId ?? defaultVenueId);
      return;
    }
    setSelectedDates(selectableDates.length === 1 ? selectableDates : []);
    applySpeaker({});
    setTheme('');
    applyReadings([]);
    setDescription('');
    initialImageKeyRef.current = null;
    setImageKey(null);
    setImagePreview(null);
    setStartTime(defaultStartTime ?? '10:00');
    setEndTime(defaultEndTime ?? '12:00');
    setVenueId(defaultVenueId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when dialog opens or switches record
  }, [open, detail?.id, detail?.imageKey, detail?.imageUrl]);

  // Refresh the selected speaker label once minister and member lists load.
  useEffect(() => {
    if (!open || !detail) return;
    if (detail.speakerMemberId && !ministers.data) return;
    applySpeaker(detail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ministers.dataUpdatedAt, members.dataUpdatedAt, open, detail?.id]);

  const dateOptions = useMemo(
    () =>
      selectableDates.map((date) => ({
        value: date,
        label: formatDate(date),
      })),
    [selectableDates],
  );

  const ministerRows = ministers.data?.data ?? [];
  const memberRows = members.data?.data ?? [];

  const speakerOptions = useMemo(
    () => buildWorshipMemberOptions(ministerRows, memberRows, speakerQuery),
    [ministerRows, memberRows, speakerQuery],
  );

  const timeRangeInvalid = isProgramTimeRangeInvalid(startTime, endTime);
  const timeRangeOvernight = isOvernightProgramTimeRange(startTime, endTime);
  const canSubmit = selectedDates.length > 0 && !timeRangeInvalid;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['programs', programId, 'day-details'] });
    void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const scriptureRefs = [reading1, reading2, reading3]
        .map((ref) => ref.trim())
        .filter((ref) => ref.length > 0);
      const isGuest = Boolean(speakerId?.startsWith('guest:'));
      const imagePatch = editing
        ? imageKey !== initialImageKeyRef.current
          ? { imageKey }
          : {}
        : { imageKey };
      const payload = {
        dates: [...selectedDates].sort(),
        speakerMemberId: speakerId && !isGuest ? speakerId : null,
        guestSpeaker: isGuest ? selectedSpeaker?.label.trim() || null : null,
        theme: theme.trim() || null,
        scriptureRefs,
        description: description.trim() || null,
        ...imagePatch,
        startTime,
        endTime,
        venueId,
      };
      if (editing) {
        await api.patch<ProgramDayDetail>(
          `/programs/${programId}/day-details/${editing.id}`,
          payload,
        );
      } else {
        await api.post<ProgramDayDetail>(`/programs/${programId}/day-details`, payload);
      }
      if (enableWorship) {
        await worshipRef.current?.applyDraft();
      }
    },
    onSuccess: () => {
      toast.success(t('dayDetails.saved'));
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/programs/${programId}/day-details/${editing!.id}`),
    onSuccess: () => {
      toast.success(t('dayDetails.deleted'));
      setDeleteOpen(false);
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const readingsSection = (
    <section className="space-y-3">
      <div>
        <Label>{t('dayDetails.readingsLabel')}</Label>
        <p className="mt-1 text-caption text-fg-secondary">{t('dayDetails.readingsHint')}</p>
      </div>
      {(
        [
          ['reading1', reading1, setReading1, t('dayDetails.reading1')],
          ['reading2', reading2, setReading2, t('dayDetails.reading2')],
          ['reading3', reading3, setReading3, t('dayDetails.reading3')],
        ] as const
      ).map(([key, value, onChange, label]) => (
        <div key={key} className="space-y-1.5">
          <Label className="text-caption text-fg-secondary">{label}</Label>
          <ScriptureRefPicker
            id={`day-detail-${key}`}
            value={value}
            onChange={onChange}
            allowRange
            popoverPortal="dialog"
            labels={scriptureRefPickerLabels(tContent, tCommon('action.clearSelection'))}
          />
        </div>
      ))}
    </section>
  );

  const speakerSection = (
    <section className="flex flex-col rounded-lg border border-border bg-surface-secondary/40 p-4">
      <div>
        <p className="text-card-title text-fg">{t('dayDetails.speaker')}</p>
        <p className="text-caption text-fg-secondary">{t('dayDetails.speakerPanelDescription')}</p>
      </div>
      <div className="mt-4">
        <EntityCombobox
          value={speakerId}
          onChange={(id) => {
            if (!id) {
              setSpeakerId(null);
              setSelectedSpeaker(null);
              return;
            }
            const option =
              speakerOptions.find((item) => item.id === id) ??
              (selectedSpeaker?.id === id ? selectedSpeaker : null);
            if (!option) return;
            setSpeakerId(id);
            setSelectedSpeaker(option);
          }}
          options={speakerOptions}
          selected={selectedSpeaker}
          onQueryChange={setSpeakerQuery}
          loading={
            (ministers.isPending && !ministers.data) || (members.isPending && !members.data)
          }
          placeholder={t('dayDetails.speakerPlaceholder')}
          emptyMessage={t('dayDetails.speakerEmpty')}
          clearLabel={tCommon('action.clearSelection')}
          popoverPortal="dialog"
          createAction={
            speakerQuery.trim() &&
            !speakerOptions.some(
              (option) => option.label.toLowerCase() === speakerQuery.trim().toLowerCase(),
            )
              ? {
                  label: t('dayDetails.speakerGuest', { name: speakerQuery.trim() }),
                  onClick: (query) => {
                    const name = query.trim();
                    if (!name) return;
                    const option = { id: guestSpeakerId(name), label: name };
                    setSpeakerId(option.id);
                    setSelectedSpeaker(option);
                    setSpeakerQuery('');
                  },
                }
              : undefined
          }
        />
      </div>
    </section>
  );

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={editing ? t('dayDetails.edit') : t('dayDetails.create')}
        size="2xl"
        bodyClassName="overflow-visible"
        closeLabel={tCommon('action.close')}
        cancelLabel={tCommon('action.cancel')}
        submitLabel={editing ? t('dayDetails.save') : t('dayDetails.add')}
        submitting={save.isPending}
        submitDisabled={!canSubmit}
        footerLeading={
          editing ? (
            <button
              type="button"
              className="text-body-sm text-destructive hover:underline"
              disabled={save.isPending || remove.isPending}
              onClick={() => setDeleteOpen(true)}
            >
              {t('dayDetails.delete')}
            </button>
          ) : undefined
        }
        onSubmit={() => {
          if (!canSubmit || timeRangeInvalid) return;
          save.mutate();
        }}
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-6">
          <section className="space-y-4">
            <div>
              <p className="text-body-sm font-medium text-fg">{t('dayDetails.sectionSchedule')}</p>
              <p className="text-caption text-fg-secondary">{t('dayDetails.sectionScheduleHint')}</p>
            </div>

            <div className="flex flex-col gap-2">
              <Label>{t('dayDetails.dates')}</Label>
              <Tip tipId="programs.dayDetails.dates" />
              {selectableDates.length === 0 ? (
                <p className="text-body-sm text-fg-secondary">{t('dayDetails.noSelectableDates')}</p>
              ) : (
                <MultiSelectListbox
                  value={selectedDates}
                  onChange={setSelectedDates}
                  options={dateOptions}
                  placeholder={t('dayDetails.datesPlaceholder')}
                  searchable={selectableDates.length > 8}
                  searchPlaceholder={t('dayDetails.datesSearch')}
                  emptyMessage={t('dayDetails.noSelectableDates')}
                  summaryLabel={(count) => t('dayDetails.selectedCount', { count })}
                  selectAllLabel={t('dayDetails.selectAllDates')}
                  clearAllLabel={tCommon('action.clearSelection')}
                  removeLabel={tCommon('action.remove')}
                  maxChips={4}
                  invalid={selectedDates.length === 0}
                  popoverPortal="dialog"
                />
              )}
              {selectedDates.length === 0 ? (
                <FormFieldError message={t('dayDetails.datesRequired')} />
              ) : null}
            </div>

            <div className="space-y-1.5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <Label>{t('startTime')}</Label>
                  <TimePicker
                    value={startTime}
                    onChange={(value) => setStartTime(value ?? '10:00')}
                    popoverPortal="dialog"
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                  <Label>{t('endTime')}</Label>
                  <TimePicker
                    value={endTime}
                    onChange={(value) => setEndTime(value ?? '12:00')}
                    popoverPortal="dialog"
                  />
                </div>
              </div>
              <Tip tipId="programs.churchTimezone" />
              {timeRangeOvernight ? (
                <p className="text-caption text-fg-secondary">{t('timeRangeOvernightHint')}</p>
              ) : null}
              {timeRangeInvalid ? <FormFieldError message={t('timeRangeInvalid')} /> : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('venue')}</Label>
              <VenueSelect value={venueId} onChange={setVenueId} popoverPortal="dialog" />
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <div>
              <p className="text-body-sm font-medium text-fg">{t('dayDetails.sectionContent')}</p>
              <p className="text-caption text-fg-secondary">
                {enableWorship
                  ? t('dayDetails.sectionContentHintService')
                  : t('dayDetails.sectionContentHint')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('dayDetails.theme')}</Label>
              <Input value={theme} onChange={(e) => setTheme(e.target.value)} />
            </div>

            {enableWorship ? (
              <p className="text-caption text-fg-secondary">{t('dayDetails.speakerInWorship')}</p>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <Label>{t('dayDetails.descriptionField')}</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>

            <ProgramDayImageField
              idPrefix="day-detail"
              imageKey={imageKey}
              previewUrl={imagePreview}
              onChange={({ imageKey: nextKey, previewUrl }) => {
                setImageKey(nextKey);
                setImagePreview(previewUrl);
              }}
            />
          </section>
          </div>

          <aside className="flex min-w-0 flex-col gap-6 lg:border-l lg:border-border lg:pl-6">
            {readingsSection}
            {enableWorship ? (
              <WorshipPlanningSection
                ref={worshipRef}
                programId={programId}
                dates={selectedDates}
                deferSave
                hydrateExisting={Boolean(editing)}
                embedded
                sidePanel
              />
            ) : (
              speakerSection
            )}
          </aside>
        </div>
      </FormDialog>

      {editing ? (
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title={t('dayDetails.confirmDelete.title')}
          description={t('dayDetails.confirmDelete.description')}
          confirmLabel={t('dayDetails.confirmDelete.confirm')}
          cancelLabel={tCommon('action.cancel')}
          destructive
          loading={remove.isPending}
          onConfirm={() => remove.mutate()}
        />
      ) : null}
    </>
  );
};
